import { useState, useCallback } from "react";
import type { Step, SelectionType, ShowWhen, ExclusionCondition } from "../types/configurator";
import { stepId, itemId } from "../utils";

export interface UseStepsReturn {
  steps: Step[];
  resetSteps: (steps: Step[]) => void;
  addStep: () => void;
  duplicateStep: (id: string) => void;
  removeStep: (id: string) => void;
  updateStep: (id: string, updates: Partial<Step>) => void;
  reorderStep: (fromId: string, toId: string) => void;
  addProductToStep: (stepId: string, productId: string) => void;
  addFieldValueToStep: (stepId: string, field: string, value: string, label: string) => void;
  removeProductFromStep: (stepId: string, itemUid: string) => void;
  setConditions: (stepId: string, conditions: ShowWhen[]) => void;
  setExclusions: (stepId: string, exclusions: ExclusionCondition[]) => void;
  getStepIndex: (id: string) => number;
  getPriorSteps: (id: string) => Step[];
}

const DEFAULT_STEPS: Step[] = [
  {
    id: stepId(),
    title: "Choose Your Sauna",
    description: "",
    breadcrumb: "",
    proTip: "",
    selectionType: "single",
    required: true,
    conditions: [],
    exclusions: [],
    items: [
      { uid: itemId(), type: 'product', productId: "hs-001" },
      { uid: itemId(), type: 'product', productId: "hs-002" },
      { uid: itemId(), type: 'product', productId: "hs-003" },
      { uid: itemId(), type: 'product', productId: "hs-004" },
    ],
  },
  {
    id: stepId(),
    title: "Select Wood Finish",
    description: "",
    breadcrumb: "",
    proTip: "",
    selectionType: "single",
    required: true,
    conditions: [],
    exclusions: [],
    items: [
      { uid: itemId(), type: 'product', productId: "hs-005" },
      { uid: itemId(), type: 'product', productId: "hs-006" },
      { uid: itemId(), type: 'product', productId: "hs-007" },
    ],
  },
  {
    id: stepId(),
    title: "Pick Your Heater",
    description: "",
    breadcrumb: "",
    proTip: "",
    selectionType: "single",
    required: true,
    conditions: [],
    exclusions: [],
    items: [
      { uid: itemId(), type: 'product', productId: "hs-009" },
      { uid: itemId(), type: 'product', productId: "hs-010" },
      { uid: itemId(), type: 'product', productId: "hs-011" },
    ],
  },
  {
    id: stepId(),
    title: "Accessories",
    description: "",
    breadcrumb: "",
    proTip: "",
    selectionType: "multi",
    required: true,
    conditions: [],
    exclusions: [],
    items: [
      { uid: itemId(), type: 'product', productId: "hs-012" },
      { uid: itemId(), type: 'product', productId: "hs-013" },
      { uid: itemId(), type: 'product', productId: "hs-014" },
      { uid: itemId(), type: 'product', productId: "hs-015" },
      { uid: itemId(), type: 'product', productId: "hs-016" },
    ],
  },
];

export function useSteps(initial?: Step[]): UseStepsReturn {
  const [steps, setSteps] = useState<Step[]>(initial ?? DEFAULT_STEPS);

  const resetSteps = useCallback((newSteps: Step[]) => {
    setSteps(newSteps);
  }, []);

  const addStep = useCallback(() => {
    setSteps((prev) => [
      ...prev,
      {
        id: stepId(),
        title: "",
        description: "",
        breadcrumb: "",
        proTip: "",
        selectionType: "single" as SelectionType,
        required: true,
        conditions: [],
    exclusions: [],
        items: [],
        priority: 0,
      },
    ]);
  }, []);

  const duplicateStep = useCallback((id: string) => {
    setSteps((prev) => {
      const idx = prev.findIndex((s) => s.id === id);
      if (idx === -1) return prev;
      const source = prev[idx];
      const copy: Step = {
        ...source,
        id: stepId(),
        title: `${source.title} (copy)`,
        items: source.items.map((item) => ({ ...item, uid: itemId() })),
        conditions: [],
    exclusions: [],
      };
      const next = [...prev];
      next.splice(idx + 1, 0, copy);
      return next;
    });
  }, []);

  const removeStep = useCallback((id: string) => {
    setSteps((prev) => {
      const remaining = prev.filter((s) => s.id !== id);
      // Clean up any conditions that referenced the deleted step
      return remaining.map((s) => ({
        ...s,
        conditions: s.conditions.filter((c) => c.stepId !== id),
      }));
    });
  }, []);

  const updateStep = useCallback((id: string, updates: Partial<Step>) => {
    setSteps((prev) =>
      prev.map((s) => (s.id === id ? { ...s, ...updates } : s))
    );
  }, []);

  const reorderStep = useCallback((fromId: string, toId: string) => {
    if (fromId === toId) return;
    setSteps((prev) => {
      const arr = [...prev];
      const fromIdx = arr.findIndex((s) => s.id === fromId);
      const toIdx = arr.findIndex((s) => s.id === toId);
      if (fromIdx === -1 || toIdx === -1) return prev;
      const [moved] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, moved);
      return arr;
    });
  }, []);

  const addProductToStep = useCallback((targetStepId: string, productId: string) => {
    setSteps((prev) =>
      prev.map((s) => {
        if (s.id !== targetStepId) return s;
        // Don't add duplicates
        if (s.items.some((i) => i.type === 'product' && i.productId === productId)) return s;
        return {
          ...s,
          items: [...s.items, { uid: itemId(), type: 'product' as const, productId }],
        };
      })
    );
  }, []);

  const addFieldValueToStep = useCallback(
    (targetStepId: string, field: string, value: string, label: string) => {
      setSteps((prev) =>
        prev.map((s) => {
          if (s.id !== targetStepId) return s;
          // Don't add duplicates
          if (s.items.some((i) => i.type === 'field_value' && i.field === field && i.value === value)) return s;
          return {
            ...s,
            items: [...s.items, { uid: itemId(), type: 'field_value' as const, field, value, label }],
          };
        })
      );
    },
    []
  );

  const removeProductFromStep = useCallback((targetStepId: string, uid: string) => {
    setSteps((prev) =>
      prev.map((s) =>
        s.id === targetStepId
          ? { ...s, items: s.items.filter((i) => i.uid !== uid) }
          : s
      )
    );
  }, []);

  const setConditions = useCallback(
    (targetStepId: string, conditions: ShowWhen[]) => {
      setSteps((prev) =>
        prev.map((s) => s.id === targetStepId ? { ...s, conditions } : s)
      );
    },
    []
  );

  const setExclusions = useCallback(
    (targetStepId: string, exclusions: ExclusionCondition[]) => {
      setSteps((prev) =>
        prev.map((s) => s.id === targetStepId ? { ...s, exclusions } : s)
      );
    },
    []
  );

  const getStepIndex = useCallback(
    (id: string) => steps.findIndex((s) => s.id === id),
    [steps]
  );

  const getPriorSteps = useCallback(
    (id: string) => {
      const idx = steps.findIndex((s) => s.id === id);
      return steps.slice(0, idx);
    },
    [steps]
  );

  return {
    steps,
    resetSteps,
    addStep,
    duplicateStep,
    removeStep,
    updateStep,
    reorderStep,
    addProductToStep,
    addFieldValueToStep,
    removeProductFromStep,
    setConditions,
    setExclusions,
    getStepIndex,
    getPriorSteps,
  };
}
